import React, { createContext, useContext, useState } from 'react';

const translations = {
  en: {
    // Navbar
    nav: {
      topBar: 'Serving Miami & South Florida',
      phone: '(786) 972-3215',
      email: 'hello@reliable-corp.com',
      links: ['About', 'Services', 'Portfolio', 'Testimonials', 'Contact'],
      hrefs: ['#about', '#services', '#portfolio', '#testimonials', '#contact'],
      cta: 'Get a Free Quote',
      menu: 'Menu',
      langToggle: 'ES',
    },
    // Hero
    hero: {
      slides: [
        {
          headline: 'Building\nExcellence\nFrom Ground Up',
          subtext:
            'Commercial construction and facility maintenance tailored for hospitals, corporate campuses, and institutional facilities across South Florida.',
          tag: 'Commercial Construction',
        },
        {
          headline: 'Precision\nMaintenance\nYou Can Rely On',
          subtext:
            'Comprehensive facility maintenance programs designed to keep your operations running without disruption — 24/7 support available.',
          tag: 'Facility Maintenance',
        },
        {
          headline: 'Trusted By\nInstitutions\nThat Demand More',
          subtext:
            'From hospitals to high-rise offices — we deliver the highest standards of safety, quality, and professionalism on every project.',
          tag: 'Healthcare & Institutional',
        },
      ],
      badges: ['Licensed & Insured', 'OSHA Certified', 'Minority-Owned Business', 'FL State Licensed'],
      ctaPrimary: 'Get a Free Quote',
      ctaPhone: '(786) 972-3215',
    },
    // Stats
    stats: [
      { value: '500+', label: 'Projects Completed' },
      { value: '200+', label: 'Clients Served' },
      { value: '24/7', label: 'Emergency Support' },
      { value: '100%', label: 'Licensed & Insured' },
    ],
    // Services
    services: {
      eyebrow: 'What We Do',
      heading: 'Our Services',
      subtext:
        'We deliver full-spectrum construction and maintenance solutions — built for the rigorous demands of commercial, healthcare, and institutional environments.',
      cta: 'Request This Service',
      bottomCta: {
        heading: "Don't see your specific need?",
        sub: "We handle a wide range of specialty projects. Let's talk about what you need.",
        btn: 'Contact Us Today',
      },
      items: [
        {
          title: 'Commercial Construction',
          description:
            'Full-scale commercial build-outs and ground-up construction for offices, retail, and multi-use facilities. We manage every phase from permitting to final inspection.',
          tags: ['Office Fit-Outs', 'Retail Spaces', 'Ground-Up Builds'],
        },
        {
          title: 'Healthcare Facilities',
          description:
            'Specialized construction and renovation for hospitals, clinics, and medical offices. We adhere to ICRA protocols and infection control standards throughout every project.',
          tags: ['Hospitals', 'Clinics', 'Medical Offices'],
        },
        {
          title: 'Facility Maintenance',
          description:
            'Comprehensive planned preventive maintenance programs to keep your facilities running at peak performance. Reactive maintenance available 24/7.',
          tags: ['Preventive Programs', '24/7 Response', 'Inspections'],
        },
        {
          title: 'Electrical Services',
          description:
            'Licensed electrical contracting for commercial and institutional buildings. Panel upgrades, lighting retrofits, emergency power systems, and code compliance.',
          tags: ['Panel Upgrades', 'Lighting Retrofit', 'Emergency Power'],
        },
        {
          title: 'Plumbing & Mechanical',
          description:
            'Full plumbing installation, repair, and system upgrades for commercial properties. Backflow prevention, water treatment, and fixture upgrades.',
          tags: ['Pipe Installation', 'Backflow', 'Fixture Upgrades'],
        },
        {
          title: 'HVAC Systems',
          description:
            'Design, installation, and maintenance of heating, ventilation, and air conditioning systems optimized for energy efficiency and occupant comfort.',
          tags: ['Installation', 'Maintenance', 'Energy Audits'],
        },
      ],
    },
    // About
    about: {
      eyebrow: 'About Us',
      heading: 'Reliable By Name,\nReliable By Nature',
      p1: 'Founded in 2020, Reliable Maintenance & Construction Corp has built its reputation on delivering complex commercial and institutional projects with precision, professionalism, and an unwavering commitment to safety.',
      p2: 'We specialize in environments where the margin for error is zero — healthcare facilities, occupied office buildings, government institutions, and high-end commercial spaces across Miami and South Florida.',
      values: [
        { label: 'Safety First', text: 'OSHA-compliant protocols on every job site, zero compromise.' },
        { label: 'Quality Craftsmanship', text: 'We hold ourselves to the highest standards of workmanship and finish.' },
        { label: 'Client Partnership', text: 'Transparent communication from estimate to final walkthrough.' },
      ],
      certsLabel: 'Licenses & Certifications',
      certs: [
        'Licensed General Contractor — FL',
        'OSHA 30-Hour Certified',
        'MBE Certified (Minority Business Enterprise)',
        'ICRA Certified — Healthcare Construction',
        'EPA Lead-Safe Certified',
        'Fully Bonded & Insured ($5M+)',
      ],
      cta: 'Work With Us',
      yearsLabel: 'Years of\nExperience',
    },
    // Portfolio
    portfolio: {
      eyebrow: 'Our Work',
      heading: 'Featured Projects',
      cta: 'Start Your Project',
      categories: ['All', 'Healthcare', 'Commercial', 'Institutional', 'Maintenance'],
      inquire: 'Inquire About Similar Work',
      durationLabel: 'Duration',
      valueLabel: 'Value',
      locationLabel: 'Location',
      projects: [
        {
          title: 'Regional Medical Center — 4th Floor Renovation',
          category: 'Healthcare',
          location: 'Miami, FL',
          scope:
            'Full floor gut renovation of occupied hospital unit including patient rooms, nursing stations, and corridors. ICRA-compliant throughout.',
          duration: '8 Months',
          value: '$2.4M',
        },
        {
          title: 'Corporate HQ Office Build-Out',
          category: 'Commercial',
          location: 'Brickell, FL',
          scope:
            'Complete interior fit-out of 22,000 sq ft corporate headquarters including executive suites, open plan areas, and conference facilities.',
          duration: '5 Months',
          value: '$1.8M',
        },
        {
          title: 'Public School District — Multi-Campus Maintenance',
          category: 'Institutional',
          location: 'Miami-Dade County, FL',
          scope:
            'Annual maintenance contract covering HVAC, plumbing, electrical, and general repairs across 6 school campuses.',
          duration: 'Ongoing',
          value: '$900K/yr',
        },
        {
          title: 'Ambulatory Surgery Center — New Build',
          category: 'Healthcare',
          location: 'Doral, FL',
          scope:
            'Ground-up construction of a state-of-the-art ambulatory surgery center with 4 OR suites, recovery bays, and administrative wing.',
          duration: '14 Months',
          value: '$6.1M',
        },
        {
          title: 'Class-A Office Tower — Tenant Improvements',
          category: 'Commercial',
          location: 'Downtown Miami, FL',
          scope:
            'Multi-tenant fit-out across floors 8–12 of a 30-story commercial tower. Coordinated with building management for minimal disruption.',
          duration: '6 Months',
          value: '$3.2M',
        },
        {
          title: 'Planned Preventive Maintenance — Mixed-Use Portfolio',
          category: 'Maintenance',
          location: 'South Florida',
          scope:
            '12-property portfolio maintenance program covering routine inspections, emergency response, and capital improvement planning.',
          duration: 'Ongoing',
          value: '$1.5M/yr',
        },
      ],
    },
    // Testimonials
    testimonials: {
      eyebrow: 'Client Reviews',
      heading: 'What Our Clients Say',
      subtext:
        'We measure our success by the confidence our clients place in us. Here\'s what the people and institutions we\'ve served have to say.',
      ratingText: '4.9/5 average rating across 120+ reviews',
      trustLabel: 'Trusted By Leading Organizations',
      items: [
        {
          name: 'Marcus T.',
          company: '',
          industry: 'Healthcare',
          quote:
            'Reliable completed a full floor renovation in an occupied hospital wing — on time, within budget, and with zero infection control incidents. Their ICRA protocols were flawless. We have already contracted them for our next two phases.',
          rating: 5,
          initials: 'MT',
        },
        {
          name: 'Sandra L.',
          company: '',
          industry: 'Commercial',
          quote:
            'We needed a contractor who could handle the complexity of a multi-floor office fit-out in a live building with minimal disruption. Reliable delivered exactly that — professional, communicative, and detail-oriented.',
          rating: 5,
          initials: 'SL',
        },
        {
          name: 'David R.',
          company: '',
          industry: 'Institutional',
          quote:
            'The team at Reliable Maintenance & Construction has managed our facilities maintenance contract for three years. Their responsiveness, quality of work, and accountability have been outstanding.',
          rating: 5,
          initials: 'DR',
        },
        {
          name: 'Patricia M.',
          company: '',
          industry: 'Commercial Real Estate',
          quote:
            'We manage 18 commercial properties and Reliable handles all of our preventive maintenance. They are proactive, thorough, and always available when an emergency arises. True partners.',
          rating: 5,
          initials: 'PM',
        },
      ],
    },
    // Contact
    contact: {
      eyebrow: 'Get In Touch',
      heading: "Let's Build\nSomething Together",
      subtext:
        "Whether you have a project ready to bid, need a maintenance partner, or just want to learn more about what we do — we'd love to hear from you. All inquiries receive a response within one business day.",
      infoItems: [
        { label: 'Call Us', value: '(786) 972-3215 · (786) 203-3969', sub: 'Emergency line available 24/7' },
        { label: 'Email Us', value: 'hello@reliable-corp.com', sub: 'We respond within 1 business day' },
        { label: 'Emergency', value: '24/7 Emergency Service', sub: 'Call anytime for urgent needs' },
        { label: 'Service Area', value: 'Miami & South Florida', sub: 'Broward, Palm Beach & more' },
      ],
      formTitle: 'Request a Free Consultation',
      fields: {
        name: 'Full Name',
        company: 'Company / Organization',
        email: 'Email Address',
        phone: 'Phone Number',
        projectType: 'Project Type',
        message: 'Tell us about your project',
        messagePlaceholder: 'Describe your project scope, timeline, location, and any specific requirements...',
        submit: 'Submit Request',
        privacy: 'Your information is kept confidential and will never be shared.',
        successMsg: 'Thank you! We\'ve received your request and will be in touch within 1 business day.',
      },
      projectTypes: [
        'Commercial Construction',
        'Healthcare Facility',
        'Institutional / Government',
        'Office Fit-Out / Renovation',
        'Facility Maintenance Contract',
        'HVAC / Electrical / Plumbing',
        'Other',
      ],
    },
    // Footer
    footer: {
      ctaHeading: 'Ready to start your project?',
      ctaSub: 'Contact us today for a free consultation and estimate.',
      ctaBtn: 'Get a Free Quote',
      phone: '(786) 972-3215',
      about:
        'A minority-owned general contracting firm specializing in commercial construction and facility maintenance for healthcare, institutional, and corporate clients throughout Miami and South Florida.',
      quickLinks: 'Quick Links',
      servicesLabel: 'Services',
      serviceArea: 'Service Area',
      serviceAreaText: 'We operate throughout South Florida including:',
      areas: ['Miami-Dade County', 'Broward County', 'Palm Beach County', 'Monroe County', 'The Florida Keys'],
      links: ['Privacy Policy', 'Terms of Service', 'Accessibility'],
      copyright: `© ${new Date().getFullYear()} Reliable Maintenance & Construction Corp. All Rights Reserved.`,
      navLabels: ['About', 'Services', 'Portfolio', 'Testimonials', 'Contact'],
      services: [
        'Commercial Construction',
        'Healthcare Facilities',
        'Facility Maintenance',
        'Electrical Services',
        'Plumbing & Mechanical',
        'HVAC Systems',
      ],
    },
  },

  es: {
    nav: {
      topBar: 'Sirviendo Miami y el Sur de Florida',
      phone: '(786) 972-3215',
      email: 'hello@reliable-corp.com',
      links: ['Nosotros', 'Servicios', 'Proyectos', 'Testimonios', 'Contacto'],
      hrefs: ['#about', '#services', '#portfolio', '#testimonials', '#contact'],
      cta: 'Cotización Gratis',
      menu: 'Menú',
      langToggle: 'EN',
    },
    hero: {
      slides: [
        {
          headline: 'Construyendo\nExcelencia\nDesde la Base',
          subtext:
            'Construcción comercial y mantenimiento de instalaciones para hospitales, campus corporativos e instituciones en todo el Sur de Florida.',
          tag: 'Construcción Comercial',
        },
        {
          headline: 'Mantenimiento\nde Precisión\nEn Quien Confiar',
          subtext:
            'Programas integrales de mantenimiento preventivo para mantener sus operaciones funcionando sin interrupciones — soporte disponible 24/7.',
          tag: 'Mantenimiento de Instalaciones',
        },
        {
          headline: 'Confiados Por\nInstituciones Que\nExigen Más',
          subtext:
            'Desde hospitales hasta edificios de oficinas — ofrecemos los más altos estándares de seguridad, calidad y profesionalismo en cada proyecto.',
          tag: 'Salud e Institucional',
        },
      ],
      badges: ['Licenciado y Asegurado', 'Certificado OSHA', 'Empresa Minoritaria', 'Licencia FL'],
      ctaPrimary: 'Cotización Gratis',
      ctaPhone: '(786) 972-3215',
    },
    stats: [
      { value: '500+', label: 'Proyectos Completados' },
      { value: '200+', label: 'Clientes Atendidos' },
      { value: '24/7', label: 'Soporte de Emergencia' },
      { value: '100%', label: 'Licenciado y Asegurado' },
    ],
    services: {
      eyebrow: 'Lo Que Hacemos',
      heading: 'Nuestros Servicios',
      subtext:
        'Ofrecemos soluciones integrales de construcción y mantenimiento — diseñadas para las exigencias de entornos comerciales, sanitarios e institucionales.',
      cta: 'Solicitar Este Servicio',
      bottomCta: {
        heading: '¿No ve lo que necesita?',
        sub: 'Manejamos una amplia gama de proyectos especializados. Hablemos de lo que necesita.',
        btn: 'Contáctenos Hoy',
      },
      items: [
        {
          title: 'Construcción Comercial',
          description:
            'Proyectos comerciales a gran escala para oficinas, comercios y edificios de uso mixto. Manejamos cada fase desde los permisos hasta la inspección final.',
          tags: ['Oficinas', 'Comercios', 'Construcción Nueva'],
        },
        {
          title: 'Instalaciones de Salud',
          description:
            'Construcción y remodelación especializada para hospitales, clínicas y consultorios médicos. Cumplimos con los protocolos ICRA de control de infecciones.',
          tags: ['Hospitales', 'Clínicas', 'Consultorios'],
        },
        {
          title: 'Mantenimiento de Instalaciones',
          description:
            'Programas de mantenimiento preventivo para que sus instalaciones funcionen al máximo rendimiento. Mantenimiento reactivo disponible 24/7.',
          tags: ['Programas Preventivos', 'Respuesta 24/7', 'Inspecciones'],
        },
        {
          title: 'Servicios Eléctricos',
          description:
            'Contratación eléctrica licenciada para edificios comerciales e institucionales. Actualizaciones de paneles, retrofit de iluminación y cumplimiento de normas.',
          tags: ['Paneles', 'Iluminación', 'Energía de Emergencia'],
        },
        {
          title: 'Plomería y Mecánica',
          description:
            'Instalación, reparación y mejora de sistemas de plomería para propiedades comerciales. Prevención de reflujo y mejoras de accesorios.',
          tags: ['Instalación', 'Prevención de Reflujo', 'Accesorios'],
        },
        {
          title: 'Sistemas HVAC',
          description:
            'Diseño, instalación y mantenimiento de sistemas de climatización optimizados para eficiencia energética y confort de los ocupantes.',
          tags: ['Instalación', 'Mantenimiento', 'Auditorías Energéticas'],
        },
      ],
    },
    about: {
      eyebrow: 'Nosotros',
      heading: 'Confiables de Nombre,\nConfiables de Hecho',
      p1: 'Fundada en 2020, Reliable Maintenance & Construction Corp ha construido su reputación entregando proyectos complejos con precisión, profesionalismo y un compromiso inquebrantable con la seguridad.',
      p2: 'Nos especializamos en entornos donde el margen de error es cero — instalaciones de salud, edificios de oficinas ocupados, instituciones gubernamentales y espacios comerciales de alto nivel en Miami y el Sur de Florida.',
      values: [
        { label: 'Seguridad Primero', text: 'Protocolos conformes con OSHA en cada obra, sin compromiso.' },
        { label: 'Calidad Artesanal', text: 'Nos mantenemos en los más altos estándares de mano de obra y acabado.' },
        { label: 'Alianza con el Cliente', text: 'Comunicación transparente desde el presupuesto hasta la entrega final.' },
      ],
      certsLabel: 'Licencias y Certificaciones',
      certs: [
        'Contratista General Licenciado — FL',
        'Certificación OSHA 30 Horas',
        'Certificación MBE (Empresa Minoritaria)',
        'Certificación ICRA — Construcción en Salud',
        'Certificación EPA Lead-Safe',
        'Completamente Afianzado y Asegurado ($5M+)',
      ],
      cta: 'Trabaja Con Nosotros',
      yearsLabel: 'Años de\nExperiencia',
    },
    portfolio: {
      eyebrow: 'Nuestro Trabajo',
      heading: 'Proyectos Destacados',
      cta: 'Iniciar Su Proyecto',
      categories: ['Todos', 'Salud', 'Comercial', 'Institucional', 'Mantenimiento'],
      inquire: 'Consultar Trabajo Similar',
      durationLabel: 'Duración',
      valueLabel: 'Valor',
      locationLabel: 'Ubicación',
      projects: [
        {
          title: 'Centro Médico Regional — Renovación Piso 4',
          category: 'Salud',
          location: 'Miami, FL',
          scope:
            'Renovación completa de una planta hospitalaria ocupada incluyendo habitaciones de pacientes, estaciones de enfermería y pasillos. Cumplimiento ICRA en todo momento.',
          duration: '8 Meses',
          value: '$2.4M',
        },
        {
          title: 'Sede Corporativa — Habilitación de Oficinas',
          category: 'Comercial',
          location: 'Brickell, FL',
          scope:
            'Habilitación interior completa de 22,000 pies cuadrados de sede corporativa incluyendo suites ejecutivas, áreas abiertas y salas de conferencias.',
          duration: '5 Meses',
          value: '$1.8M',
        },
        {
          title: 'Distrito Escolar — Mantenimiento Multi-Campus',
          category: 'Institucional',
          location: 'Miami-Dade, FL',
          scope:
            'Contrato anual de mantenimiento que cubre HVAC, plomería, electricidad y reparaciones generales en 6 escuelas.',
          duration: 'Continuo',
          value: '$900K/año',
        },
        {
          title: 'Centro de Cirugía Ambulatoria — Construcción Nueva',
          category: 'Salud',
          location: 'Doral, FL',
          scope:
            'Construcción desde cero de un centro de cirugía ambulatoria con 4 quirófanos, salas de recuperación y área administrativa.',
          duration: '14 Meses',
          value: '$6.1M',
        },
        {
          title: 'Torre de Oficinas Clase A — Mejoras para Inquilinos',
          category: 'Comercial',
          location: 'Downtown Miami, FL',
          scope:
            'Habilitación multi-inquilino en pisos 8–12 de una torre comercial de 30 pisos. Coordinación con la administración del edificio para mínima interrupción.',
          duration: '6 Meses',
          value: '$3.2M',
        },
        {
          title: 'Mantenimiento Preventivo — Portafolio Mixto',
          category: 'Mantenimiento',
          location: 'Sur de Florida',
          scope:
            'Programa de mantenimiento de 12 propiedades que incluye inspecciones de rutina, respuesta de emergencia y planificación de mejoras de capital.',
          duration: 'Continuo',
          value: '$1.5M/año',
        },
      ],
    },
    testimonials: {
      eyebrow: 'Opiniones de Clientes',
      heading: 'Lo Que Dicen Nuestros Clientes',
      subtext:
        'Medimos nuestro éxito por la confianza que nuestros clientes depositan en nosotros. Esto es lo que dicen quienes nos han contratado.',
      ratingText: '4.9/5 calificación promedio en 120+ reseñas',
      trustLabel: 'Confiado Por Organizaciones Líderes',
      items: [
        {
          name: 'Marcus T.',
          company: '',
          industry: 'Salud',
          quote:
            'Reliable completó una renovación completa de planta en un ala hospitalaria ocupada — a tiempo, dentro del presupuesto y sin incidentes de control de infecciones. Sus protocolos ICRA fueron impecables.',
          rating: 5,
          initials: 'MT',
        },
        {
          name: 'Sandra L.',
          company: '',
          industry: 'Comercial',
          quote:
            'Necesitábamos un contratista capaz de manejar la complejidad de una habilitación multi-piso en un edificio activo con mínima interrupción. Reliable lo entregó exactamente así — profesional, comunicativo y detallista.',
          rating: 5,
          initials: 'SL',
        },
        {
          name: 'David R.',
          company: '',
          industry: 'Institucional',
          quote:
            'El equipo de Reliable Maintenance & Construction ha gestionado nuestro contrato de mantenimiento durante tres años. Su capacidad de respuesta, calidad de trabajo y responsabilidad han sido sobresalientes.',
          rating: 5,
          initials: 'DR',
        },
        {
          name: 'Patricia M.',
          company: '',
          industry: 'Bienes Raíces Comerciales',
          quote:
            'Administramos 18 propiedades comerciales y Reliable se encarga de todo nuestro mantenimiento preventivo. Son proactivos, minuciosos y siempre disponibles en emergencias. Verdaderos socios.',
          rating: 5,
          initials: 'PM',
        },
      ],
    },
    contact: {
      eyebrow: 'Contáctenos',
      heading: 'Construyamos\nAlgo Juntos',
      subtext:
        'Ya sea que tenga un proyecto listo para cotizar, necesite un socio de mantenimiento, o simplemente quiera saber más — nos encantaría escucharle. Respondemos en un día hábil.',
      infoItems: [
        { label: 'Llámenos', value: '(786) 972-3215 · (786) 203-3969', sub: 'Línea de emergencia disponible 24/7' },
        { label: 'Escríbanos', value: 'hello@reliable-corp.com', sub: 'Respondemos en 1 día hábil' },
        { label: 'Emergencias', value: 'Servicio de Emergencia 24/7', sub: 'Llame en cualquier momento' },
        { label: 'Área de Servicio', value: 'Miami y Sur de Florida', sub: 'Broward, Palm Beach y más' },
      ],
      formTitle: 'Solicite una Consulta Gratuita',
      fields: {
        name: 'Nombre Completo',
        company: 'Empresa / Organización',
        email: 'Correo Electrónico',
        phone: 'Número de Teléfono',
        projectType: 'Tipo de Proyecto',
        message: 'Cuéntenos sobre su proyecto',
        messagePlaceholder: 'Describa el alcance, cronograma, ubicación y cualquier requisito específico...',
        submit: 'Enviar Solicitud',
        privacy: 'Su información es confidencial y nunca será compartida.',
        successMsg: 'Gracias! Hemos recibido su solicitud y nos comunicaremos en 1 día hábil.',
      },
      projectTypes: [
        'Construcción Comercial',
        'Instalación de Salud',
        'Institucional / Gobierno',
        'Habilitación / Renovación de Oficinas',
        'Contrato de Mantenimiento',
        'HVAC / Eléctrico / Plomería',
        'Otro',
      ],
    },
    footer: {
      ctaHeading: '¿Listo para iniciar su proyecto?',
      ctaSub: 'Contáctenos hoy para una consulta y presupuesto gratuitos.',
      ctaBtn: 'Cotización Gratis',
      phone: '(786) 972-3215',
      about:
        'Empresa contratista general de propiedad minoritaria especializada en construcción comercial y mantenimiento de instalaciones para clientes del sector salud, institucional y corporativo en Miami y el Sur de Florida.',
      quickLinks: 'Accesos Rápidos',
      servicesLabel: 'Servicios',
      serviceArea: 'Área de Servicio',
      serviceAreaText: 'Operamos en todo el Sur de Florida incluyendo:',
      areas: ['Condado Miami-Dade', 'Condado Broward', 'Condado Palm Beach', 'Condado Monroe', 'Los Cayos de Florida'],
      links: ['Política de Privacidad', 'Términos de Servicio', 'Accesibilidad'],
      copyright: `© ${new Date().getFullYear()} Reliable Maintenance & Construction Corp. Todos los Derechos Reservados.`,
      navLabels: ['Nosotros', 'Servicios', 'Proyectos', 'Testimonios', 'Contacto'],
      services: [
        'Construcción Comercial',
        'Instalaciones de Salud',
        'Mantenimiento de Instalaciones',
        'Servicios Eléctricos',
        'Plomería y Mecánica',
        'Sistemas HVAC',
      ],
    },
  },
};

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState('en');
  const toggle = () => setLang((prev) => (prev === 'en' ? 'es' : 'en'));
  const t = translations[lang];
  return (
    <LanguageContext.Provider value={{ lang, toggle, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  return useContext(LanguageContext);
}
